import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { computeCanInstall } from '../services/install';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallState {
  isInstalled: boolean;
  isIOS: boolean;
  canInstall: boolean;
  bannerDismissed: boolean;
  iosHelpOpen: boolean;
  promptInstall: () => void;
  dismissBanner: () => void;
  openIosHelp: () => void;
  closeIosHelp: () => void;
}

const DISMISS_KEY = 'breathe-install-dismissed';

const readDismissed = (): boolean => {
  try { return localStorage.getItem(DISMISS_KEY) === 'true'; } catch { return false; }
};

const InstallContext = createContext<InstallState | null>(null);

export const useInstall = (): InstallState => {
  const ctx = useContext(InstallContext);
  if (!ctx) throw new Error('useInstall must be used within InstallProvider');
  return ctx;
};

export const InstallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useLanguage();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(readDismissed);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    const onBIP = (e: Event) => { e.preventDefault(); setDeferred(e as BeforeInstallPromptEvent); };
    const onInstalled = () => { setIsInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const canInstall = computeCanInstall({ isInstalled, isIOS, hasDeferred: !!deferred });

  const openIosHelp = useCallback(() => setIosHelpOpen(true), []);
  const closeIosHelp = useCallback(() => setIosHelpOpen(false), []);

  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, 'true'); } catch { /* ignore */ }
    setBannerDismissed(true);
  }, []);

  const promptInstall = useCallback(async () => {
    if (isIOS) { setIosHelpOpen(true); return; }
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }, [isIOS, deferred]);

  const value: InstallState = {
    isInstalled, isIOS, canInstall, bannerDismissed, iosHelpOpen,
    promptInstall, dismissBanner, openIosHelp, closeIosHelp,
  };

  return (
    <InstallContext.Provider value={value}>
      {children}
      {iosHelpOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 animate-scale-up">
            <h3 className="text-xl font-semibold text-stone-900 text-center mb-4">
              {t('install.iosTitle') || 'Installer på iPhone/iPad'}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center text-accent font-semibold text-sm">1</div>
                <div className="flex-1"><p className="text-stone-700">{t('install.iosStep1') || 'Trykk på'} {t('install.iosShare') || 'Del-knappen'}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center text-accent font-semibold text-sm">2</div>
                <div className="flex-1"><p className="text-stone-700">{t('install.iosStep2') || 'Scroll ned og trykk "Legg til på Hjem-skjerm"'}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center text-accent font-semibold text-sm">3</div>
                <div className="flex-1"><p className="text-stone-700">{t('install.iosStep3') || 'Trykk "Legg til" oppe til høyre'}</p></div>
              </div>
            </div>
            <button
              onClick={() => { closeIosHelp(); dismissBanner(); }}
              className="w-full mt-6 px-4 py-3 bg-accent text-white font-medium rounded-full hover:bg-accent-dark transition-colors"
            >
              {t('install.understood') || 'Jeg forstår'}
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes scale-up { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-up { animation: scale-up 0.2s ease-out; }
      `}</style>
    </InstallContext.Provider>
  );
};
